export type TicketFuerKorb = {
  status: string;
  faellig_am: string | null;
  wiedervorlage_am?: string | null;
  geloescht_am?: string | null;
};

/** Ticket ist final abgeschlossen (erledigt oder geschlossen). */
export const istAbgeschlossen = (t: TicketFuerKorb): boolean =>
  t.status === 'erledigt' || t.status === 'geschlossen';

/** Datumsteil (YYYY-MM-DD) eines ISO-Strings, robust gegen Zeitanteil. */
const datumsTeil = (d: string | null | undefined): string => (d || '').slice(0, 10);

/** Ticket ist überfällig: faellig_am liegt vor heute und Ticket ist nicht abgeschlossen. */
export function istUeberfaellig(t: TicketFuerKorb, heute: string): boolean {
  if (!t.faellig_am || istAbgeschlossen(t)) return false;
  return datumsTeil(t.faellig_am) < heute;
}

/** Ticket ist heute fällig: faellig_am === heute und Ticket ist nicht abgeschlossen. */
export function istHeuteFaellig(t: TicketFuerKorb, heute: string): boolean {
  if (!t.faellig_am || istAbgeschlossen(t)) return false;
  return datumsTeil(t.faellig_am) === heute;
}

/** Ticket hat eine offene Wiedervorlage: wiedervorlage_am gesetzt und Ticket ist nicht abgeschlossen. */
export function hatWiedervorlage(t: TicketFuerKorb): boolean {
  if (!t.wiedervorlage_am) return false;
  return !istAbgeschlossen(t);
}

/**
 * #542: Ticket „schlummert“ — Wiedervorlage liegt nach heute. Es verschwindet aus den normalen
 * Ansichten und Zählern und steht nur unter „Wiedervorlagen“; ab dem Wiedervorlage-Tag ist es
 * wieder überall sichtbar (wie bei den E-Mails).
 */
export function schlummert(t: TicketFuerKorb, heute: string): boolean {
  return hatWiedervorlage(t) && datumsTeil(t.wiedervorlage_am) > heute;
}

/** YYYY-MM-DD → DD.MM.YYYY, reine String-Umformung (keine Timezone-Konvertierung). */
const formatDatumDE = (datum: string): string => {
  const [jahr, monat, tag] = datum.split('-');
  if (!jahr || !monat || !tag) return '';
  return `${tag}.${monat}.${jahr}`;
};

/**
 * Relative Fällig-Formatierung für die Tickets-Tabelle: 'Heute' / 'Morgen' / 'in X Tagen'
 * (zukünftig) bzw. das formatierte Datum wenn überfällig (vor heute). '' bei fehlendem Datum.
 * Reine Funktion — heute wird als YYYY-MM-DD-String übergeben (siehe heuteStr in TicketListe.tsx).
 */
export function formatFaellig(faelligAm: string | null | undefined, heute: string): string {
  if (!faelligAm) return '';
  const datum = datumsTeil(faelligAm);
  if (datum < heute) return formatDatumDE(datum);
  if (datum === heute) return 'Heute';

  const heuteDate = new Date(`${heute}T00:00:00`);
  const faelligDate = new Date(`${datum}T00:00:00`);
  const diffTage = Math.round((faelligDate.getTime() - heuteDate.getTime()) / 86400000);
  if (diffTage === 1) return 'Morgen';
  return `in ${diffTage} Tagen`;
}
